import type { Column, RowData, RowSelectionState, SortingState, Table } from "@tanstack/react-table";
import { toTitleCase } from "../../utils/formatText";

export function clientToServerSorting(sorting: SortingState): string | undefined {
  if (sorting.length === 0) {
    return undefined;
  }

  return sorting.map(({ id, desc }) => `${desc ? "-" : ""}${id}`).join(",");
}

export function resolveLabel<TData extends RowData, TValue = unknown>(
  column: Column<TData, TValue>,
  metaKey?: "columnSelectorOptions" | "columnFilterOptions",
): string {
  const printableHeader =
    typeof column.columnDef.header === "string" ? column.columnDef.header : toTitleCase(column.id);

  if (!metaKey) {
    return printableHeader;
  }

  return column.columnDef.meta?.[metaKey]?.label ?? printableHeader;
}

export function selectionStateToSelectedIds(state: RowSelectionState): string[] {
  return Object.entries(state)
    .filter(([, isSelected]) => isSelected)
    .map(([id]) => id);
}

/**
 * This should just be table.reset(), but that's bugged in the current version of react-table.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function resetTable(table: Table<any>) {
  table.resetColumnFilters();
  table.resetColumnOrder();
  table.resetColumnVisibility();
  table.resetExpanded();
  table.resetGlobalFilter();
  table.resetPageIndex();
  table.resetPageSize();
  table.resetRowSelection();
  table.resetSorting();
  table.resetColumnSizing();
}

export const idsToVisibilityState = (
  defaultSelectedColumnIds: string[],
  allColumnIds: string[],
): Record<string, boolean> => {
  const selectedIdsSet = new Set(defaultSelectedColumnIds);
  const defaultHiddenCols: Record<string, boolean> = {};

  allColumnIds.forEach((colId) => {
    if (colId && !selectedIdsSet.has(colId)) {
      defaultHiddenCols[colId] = false;
    }
  });

  return defaultHiddenCols;
};
